import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import type {
  CreateProposalItemPayload,
  CreateProposalPayload,
  CreateProposalSectionPayload,
  PaginatedResponse,
  ProposalDetail,
  ProposalListItem,
  UpdateProposalItemPayload,
  UpdateProposalPayload,
  UpdateProposalSectionPayload,
} from "../types";

const PROPOSALS_KEY = ["proposals"] as const;

export type ProposalListParams = {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  clientCompanyId?: string;
  // Phase 1+2: canonical name for the recipient-company filter. Maps to the
  // same DB column as clientCompanyId on the server. Use this from new code.
  recipientCompanyId?: string;
  projectId?: string;
};

export function useProposalList(
  params: ProposalListParams = {},
): UseQueryResult<PaginatedResponse<ProposalListItem>> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [
      ...PROPOSALS_KEY,
      "list",
      params,
      accessToken ? "auth" : "anon",
    ],
    enabled: !!accessToken,
    queryFn: () =>
      apiFetch<PaginatedResponse<ProposalListItem>>("/proposals", {
        token: accessToken,
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          status: params.status,
          clientCompanyId: params.clientCompanyId,
          recipientCompanyId: params.recipientCompanyId,
          projectId: params.projectId,
        },
      }),
  });
}

export function useProposal(id: string): UseQueryResult<ProposalDetail> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...PROPOSALS_KEY, "detail", id, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ proposal: ProposalDetail }>(
        `/proposals/${id}`,
        { token: accessToken },
      );
      return res.proposal;
    },
  });
}

export function useCreateProposal() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateProposalPayload) =>
      apiFetch<{ proposal: ProposalDetail }>("/proposals", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...PROPOSALS_KEY] });
    },
  });
}

export function useUpdateProposal(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateProposalPayload) =>
      apiFetch<{ proposal: ProposalDetail }>(`/proposals/${id}`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...PROPOSALS_KEY] });
    },
  });
}

export function useUpdateProposalStatus() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      status,
      rejectionReason,
    }: {
      id: string;
      status: string;
      rejectionReason?: string | null;
    }) =>
      apiFetch<{ proposal: ProposalDetail }>(`/proposals/${id}/status`, {
        method: "PUT",
        body: { status, rejectionReason: rejectionReason ?? null },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...PROPOSALS_KEY] });
    },
  });
}

export function useDeleteProposal() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/proposals/${id}`, { method: "DELETE", token: accessToken }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...PROPOSALS_KEY] });
    },
  });
}

export function useDuplicateProposal() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ proposal: ProposalDetail }>(`/proposals/${id}/duplicate`, {
        method: "POST",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...PROPOSALS_KEY] });
    },
  });
}

// ── Sections ────────────────────────────────────────────────

export function useAddProposalSection(proposalId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateProposalSectionPayload) =>
      apiFetch(`/proposals/${proposalId}/sections`, {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...PROPOSALS_KEY] });
    },
  });
}

export function useUpdateProposalSection(proposalId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sectionId,
      payload,
    }: {
      sectionId: string;
      payload: UpdateProposalSectionPayload;
    }) =>
      apiFetch(`/proposals/${proposalId}/sections/${sectionId}`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...PROPOSALS_KEY] });
    },
  });
}

export function useDeleteProposalSection(proposalId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sectionId: string) =>
      apiFetch(`/proposals/${proposalId}/sections/${sectionId}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...PROPOSALS_KEY] });
    },
  });
}

export function useReorderProposalSections(proposalId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (updates: { id: string; sortOrder: number }[]) =>
      apiFetch(`/proposals/${proposalId}/sections/reorder`, {
        method: "POST",
        body: { updates },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...PROPOSALS_KEY] });
    },
  });
}

// ── Items ───────────────────────────────────────────────────

export function useAddProposalItem(proposalId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sectionId,
      payload,
    }: {
      sectionId: string;
      payload: CreateProposalItemPayload;
    }) =>
      apiFetch(`/proposals/${proposalId}/sections/${sectionId}/items`, {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...PROPOSALS_KEY] });
    },
  });
}

export function useUpdateProposalItem(proposalId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sectionId,
      itemId,
      payload,
    }: {
      sectionId: string;
      itemId: string;
      payload: UpdateProposalItemPayload;
    }) =>
      apiFetch(
        `/proposals/${proposalId}/sections/${sectionId}/items/${itemId}`,
        { method: "PUT", body: payload, token: accessToken },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...PROPOSALS_KEY] });
    },
  });
}

export function useDeleteProposalItem(proposalId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sectionId,
      itemId,
    }: {
      sectionId: string;
      itemId: string;
    }) =>
      apiFetch(
        `/proposals/${proposalId}/sections/${sectionId}/items/${itemId}`,
        { method: "DELETE", token: accessToken },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...PROPOSALS_KEY] });
    },
  });
}
